import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subject, interval } from 'rxjs';
import { takeUntil, switchMap, startWith } from 'rxjs/operators';
import { AlertsService } from 'app/modules/alerts/alerts.service';
import { trigger, transition, style, animate } from '@angular/animations';
import { SharedAlertsCacheService } from 'app/layout/common/shared-alerts-cache.service';

interface DismissalData {
    dismissedAt: number;
    dismissedAlertIds: number[];
}

@Component({
    selector: 'critical-alerts-banner',
    templateUrl: './critical-alerts-banner.component.html',
    standalone: true,
    imports: [
        CommonModule,
        MatButtonModule,
        MatIconModule,
        MatTooltipModule
    ],
    animations: [
        trigger('slideDown', [
            transition(':enter', [
                style({ height: '0', opacity: 0, overflow: 'hidden' }),
                animate('300ms ease-out', style({ height: '*', opacity: 1 }))
            ]),
            transition(':leave', [
                animate('200ms ease-in', style({ height: '0', opacity: 0, overflow: 'hidden' }))
            ])
        ])
    ]
})
export class CriticalAlertsBannerComponent implements OnInit, OnDestroy {
    private readonly STORAGE_KEY = 'criticalAlertsBannerDismissal';
    private readonly DISMISS_DURATION_MS = 60 * 60 * 1000; // 1 hour
    private readonly REFRESH_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes
    
    showBanner: boolean = false;
    criticalCount: number = 0;
    alertPreview: string = '';
    
    private _unsubscribeAll: Subject<void> = new Subject<void>();
    private currentAlertIds: number[] = [];

    constructor(
        private _alertsService: AlertsService,
        private _router: Router,
        private _sharedAlertsCache: SharedAlertsCacheService
    ) {}

    ngOnInit(): void {
        // Defer initial load by 3 seconds to prioritize other critical page loads
        // Then refresh periodically every 5 minutes
        // ✅ OPTIMIZACIÓN: Usando SharedAlertsCacheService para evitar llamadas duplicadas
        setTimeout(() => {
            this.loadCriticalAlerts();
            
            // Set up periodic refresh - cache se refresca automáticamente cada 2 minutos
            interval(this.REFRESH_INTERVAL_MS)
                .pipe(
                    switchMap(() => this._sharedAlertsCache.getCriticalAlerts(15)),
                    takeUntil(this._unsubscribeAll)
                )
                .subscribe({
                    next: (response) => this.handleAlertsResponse(response),
                    error: (error) => console.error('Error fetching critical alerts:', error)
                });
        }, 3000); // Delay 3 seconds
    }

    private loadCriticalAlerts(): void {
        // ✅ OPTIMIZACIÓN: Usando SharedAlertsCacheService para evitar llamadas duplicadas
        this._sharedAlertsCache.getCriticalAlerts(15)
        .pipe(takeUntil(this._unsubscribeAll))
        .subscribe({
            next: (response) => this.handleAlertsResponse(response),
            error: (error) => console.error('Error fetching critical alerts:', error)
        });
    }

    private handleAlertsResponse(response: any): void {
        if (response?.data && response?.pagination) {
            this.criticalCount = response.pagination.total;
            this.currentAlertIds = response.data.map(a => a.id);
            
            // Create preview text (first 2-3 alert types)
            if (response.data.length > 0) {
                const types = [...new Set(response.data.slice(0, 3).map(a => this.formatAlertType(a.alert_type)))];
                this.alertPreview = types.join(', ');
            }
            
            this.updateBannerVisibility();
        }
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    /**
     * Update banner visibility based on dismissal state and alert changes
     */
    private updateBannerVisibility(): void {
        if (this.criticalCount === 0) {
            this.showBanner = false;
            return;
        }

        const dismissalData = this.getDismissalData();
        
        // No dismissal data - show banner
        if (!dismissalData) {
            this.showBanner = true;
            return;
        }

        // Check if dismissal expired (> 1 hour ago)
        const now = Date.now();
        const dismissalExpired = (now - dismissalData.dismissedAt) > this.DISMISS_DURATION_MS;
        
        if (dismissalExpired) {
            this.clearDismissalData();
            this.showBanner = true;
            return;
        }

        // Check if there are NEW critical alerts (not in dismissed list)
        const hasNewAlerts = this.currentAlertIds.some(id => !dismissalData.dismissedAlertIds.includes(id));
        
        if (hasNewAlerts) {
            this.clearDismissalData();
            this.showBanner = true;
            return;
        }

        // Still within dismissal period and no new alerts
        this.showBanner = false;
    }

    /**
     * Dismiss banner for 1 hour
     */
    dismiss(): void {
        const dismissalData: DismissalData = {
            dismissedAt: Date.now(),
            dismissedAlertIds: [...this.currentAlertIds]
        };
        
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(dismissalData));
        this.showBanner = false;
    }

    /**
     * Navigate to alerts page with critical filter
     */
    reviewAlerts(): void {
        this._router.navigate(['/alerts'], {
            queryParams: {
                severity: 'critical',
                status: 'unresolved'
            }
        });
    }

    /**
     * Get dismissal data from localStorage
     */
    private getDismissalData(): DismissalData | null {
        try {
            const data = localStorage.getItem(this.STORAGE_KEY);
            return data ? JSON.parse(data) : null;
        } catch (error) {
            console.error('Error reading dismissal data:', error);
            return null;
        }
    }

    /**
     * Clear dismissal data from localStorage
     */
    private clearDismissalData(): void {
        localStorage.removeItem(this.STORAGE_KEY);
    }

    /**
     * Format alert type for display
     */
    private formatAlertType(type: string): string {
        const typeMap: { [key: string]: string } = {
            'impossible_travel': 'Viaje imposible',
            'blacklisted_ip': 'IP en lista negra',
            'high_risk_country': 'País de alto riesgo',
            'recommended_block': 'Bloqueo recomendado',
            'unusual_time_access': 'Acceso en horario inusual',
            'brute_force': 'Fuerza bruta',
            'suspicious_activity': 'Actividad sospechosa'
        };
        
        return typeMap[type] || type;
    }
}
